import type { HTMLAttributes, MouseEventHandler } from 'react';

import Icon, { type IconSize } from 'components/Icon';
import LinkOrButton from 'components/LinkOrButton';
import { useNumberFormatter } from 'hooks/format';
import { cn } from 'utils/classNames';
import { range } from 'utils/helpers';

import {
	defaultReviewCountVariant,
	type ReviewCount,
	type ReviewCountVariant,
	useFullReviewCountText,
	useReviewCountFormatter,
	useScreenReaderText,
} from './helpers';

interface Props extends HTMLAttributes<HTMLSpanElement> {
	/** Allow any score or count text to wrap below the stars */
	allowWrapping?: boolean;

	/** Container class names */
	className?: string;

	/** If the container should be inline or block */
	displayMode?: 'block' | 'inline';

	/** is the score visible */
	hasVisibleScore?: boolean;

	/** Link to reviews */
	href?: string;

	/** Click handler, will result in a wrapping button if `href` isn't set */
	onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;

	/** Number of reviews */
	reviewCount?: ReviewCount;

	/** Style of the review count text */
	reviewCountVariant?: ReviewCountVariant;

	/** Number of stars */
	score?: number;

	/** Stars and count text size */
	size?: 'large' | 'medium' | 'small';
}

// Style for each decimal point, e.g. 4.2 will clip its last star 65% from the
// right edge. Doesn't use steps of 10 since the star shape doesn't fill the
// icon box edge to edge.
const PARTIAL_STAR_INSETS: Record<number, string> = {
	1: '[clip-path:inset(0_70%_0_0)]',
	2: '[clip-path:inset(0_65%_0_0)]',
	3: '[clip-path:inset(0_60%_0_0)]',
	4: '[clip-path:inset(0_55%_0_0)]',
	5: '[clip-path:inset(0_50%_0_0)]',
	6: '[clip-path:inset(0_45%_0_0)]',
	7: '[clip-path:inset(0_40%_0_0)]',
	8: '[clip-path:inset(0_35%_0_0)]',
	9: '[clip-path:inset(0_30%_0_0)]',
} as const;

/** Star rating out of five and a text with the total number of reviews. */
export default function Rating({
	allowWrapping = true,
	className = '',
	displayMode = 'inline',
	hasVisibleScore = false,
	href,
	onClick,
	reviewCount = 0,
	reviewCountVariant = defaultReviewCountVariant,
	score = 0,
	size = 'medium',
	...attrs
}: Props) {
	const formatNumber = useNumberFormatter();
	const formatReviewCount = useReviewCountFormatter();
	const screenReaderText = useScreenReaderText({
		reviewCount,
		reviewCountVariant,
		score,
	});
	const fullReviewCountText = useFullReviewCountText(reviewCount);

	// E.g. 4.2 (minus floor) → 0.200000018 (toFixed) → "0.2" (get index 2) → 2
	const scoreDecimal = Number((score - Math.floor(score)).toFixed(1)[2]);
	const partialStarClass = PARTIAL_STAR_INSETS[scoreDecimal];

	const starTypes = range(5).map((i) =>
		i < Math.floor(score) ? 'full' : i < score ? 'partial' : 'empty',
	);
	const starSizes: Record<typeof size, IconSize> = {
		small: 16,
		medium: 24,
		large: 40,
	};

	const isInteractive = Boolean(href || onClick);

	const content = (
		<>
			<span
				className="inline-flex items-start"
				role="img"
				aria-label={screenReaderText}
			>
				{starTypes.map((starType, i) => {
					// Will not be sorted and there is no meaningful key.
					/* oxlint-disable react/no-array-index-key */
					const mainStar = (
						<Icon
							key={i}
							name={starType === 'full' ? 'star' : 'star-outlined'}
							size={starSizes[size]}
							className={starType === 'partial' ? 'align-top' : undefined}
						/>
					);
					if (starType === 'partial') {
						return (
							<span key={i} className="relative">
								{mainStar}
								<Icon
									name="star"
									size={starSizes[size]}
									className={cn('absolute inset-0', partialStarClass)}
								/>
							</span>
						);
					}
					return mainStar;
				})}
			</span>
			{hasVisibleScore && (
				<span
					aria-hidden
					className={cn(
						'font-bold',
						// Right padding for visual balance with the whitespace inherit in the star icons.
						reviewCountVariant === 'hidden' && 'pr-0.5',
					)}
				>
					{formatNumber(score)}
				</span>
			)}
			{reviewCountVariant !== 'hidden' && (
				<span
					// This text is always part of the stars' alt text.
					aria-hidden="true"
					className={cn(
						// Right padding for visual balance with the whitespace inherit in the star icons.
						'pr-0.5 text-nowrap',
						isInteractive && [
							reviewCountVariant === 'fullText' &&
								'underline group-hover/rating:underline-strong',
							reviewCountVariant === 'numberOnly' &&
								'group-hover/rating:underline',
						],
					)}
				>
					{reviewCountVariant === 'numberOnly'
						? `(${formatReviewCount(reviewCount)})`
						: fullReviewCountText}
				</span>
			)}
		</>
	);

	const containerClasses = cn(
		className,
		displayMode === 'block' ? 'flex' : 'inline-flex',
		'items-center align-top font-body',
		allowWrapping && 'flex-wrap',
		size === 'small' && 'gap-x-1.5 text-xs',
		size === 'medium' && 'gap-x-2 text-md',
		size === 'large' && 'gap-x-2.5 text-lg',
		isInteractive && 'group/rating',
	);

	if (isInteractive) {
		return (
			<LinkOrButton
				{...attrs}
				href={href}
				onClick={onClick}
				className={containerClasses}
			>
				{content}
			</LinkOrButton>
		);
	}

	return (
		<span {...attrs} className={containerClasses}>
			{content}
		</span>
	);
}
Rating.displayName = 'Rating';
